import { DocViewerTab } from '@enums/doc-viewer-tab.enum';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, from, map, mergeMap, Observable, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { AppConfigService } from '@services/app-config.service';
import { FileSdk } from '@sdks/file.sdk';
import { MainTabIndices } from '@enums/main-tab-indices.enum';
import { TabStates } from './tab-states';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { LexbeDoc } from '@models/doc.model';
import { UserConfigsService } from '@services/user-configs.service';
import { Role } from '@models/role.model';
import { DocumentViews } from '@store/doc-viewer/doc-viewer.state';

@Injectable({
	providedIn: 'root'
})
export class iFrameUrlFactory {
	private readonly iframeUrlMap: ReadonlyMap<DocViewerTab, UrlAndContent> =
		new Map<DocViewerTab, UrlAndContent>(
			(Object.entries(DocViewerTab)).map(([, key]) =>
				[key, {url: new BehaviorSubject(null), content: new BehaviorSubject(null)}]));
	private pageTabPage: number;
	private prodId: string;
	private tabFileTypeMap: Map<DocViewerTab, 1 | 2 | 6> = new Map<DocViewerTab, 1 | 2 | 6>([
		[DocViewerTab.Original, 1],
		[DocViewerTab.Html, 2],
		[DocViewerTab.Pdf, 6]
	]);

	private lastFetchTimes: Record<DocViewerTab, Date> = {} as Record<DocViewerTab, Date>;
	private roles = Role;

	constructor(
		private httpClient: HttpClient,
		private config: AppConfigService,
		private fileSdk: FileSdk,
		private tabStates: TabStates,
		private docViewerStore: DocViewerStore,
		private userConfigsService: UserConfigsService
	) {
		this.docViewerStore.currentDocument$.pipe(
			filter((x) => !!x),
			distinctUntilChanged((a, b) => a.docId === b.docId),
			switchMap((currentDocument) => {
				return from(this.iframeUrlMap).pipe(
					mergeMap(([key, val]) => {
						return this.buildUrl(currentDocument, key).pipe(
							map((url) => {
								val.url.next(null);
								val.content.next(URL.createObjectURL(new Blob([])));
								val.url.next(url);
							}),
							catchError(() => {
								val.url.next(null);
								val.content.next(URL.createObjectURL(new Blob([])));
								return of(null);
							})
						);
					})
				);
			})
		).subscribe();

		this.docViewerStore.currentTab$.pipe(
			withLatestFrom(this.docViewerStore.currentDocument$),
			distinctUntilChanged(([aTab, aCurrentDocument], [bTab, bCurrentDocument]) => {
				return aTab === bTab && aCurrentDocument.docId === bCurrentDocument.docId;
			}),
			switchMap(([tabIndex, document]) => {
				if (sessionStorage['useNewDocViewer'] === 'true')
					return of(null);

				if (!tabStates.enabled[tabIndex]) {
					return of(null);
				}

				const tab: DocViewerTab = Object.values(DocViewerTab)[tabIndex];
				if (this.shouldGenerateTemporaryUrl(tab)) {
					return this.updateTemporaryUrl(document.docId, tab).pipe(
						tap((newUrl) => {
							if (newUrl) {
								this.iframeUrlMap.get(tab).url.next(newUrl);

							}
							this.tabStates.mainTabSelectedIndex = tabIndex;
							this.tabStates.onMainTabChange();
						})
					);
				}
				return this.updateContent(tab).pipe(
					tap(() => {
						switch (tabIndex) {
							case MainTabIndices.Redacted:
								if (!this.userConfigsService.inRoles(this.roles.LimitedFeatureSet))
									this.tabStates.rightTabSelectedIndex = 5;
								break;
							case MainTabIndices.Annotated:
								if (!this.userConfigsService.inRoles(this.roles.LimitedFeatureSet))
									this.tabStates.rightTabSelectedIndex = 6;
								break;
						}
						this.tabStates.mainTabSelectedIndex = tabIndex;
						this.tabStates.onMainTabChange();
					})
				);
			})
		).subscribe();

		this.docViewerStore.simpleState$.pipe(
			distinctUntilChanged((a, b) => a.currentPage === b.currentPage)
		).subscribe((current) => {
			this.updatePageTabPage(current.currentPage);
		});

		this.docViewerStore.simpleState$.pipe(
			withLatestFrom(of(sessionStorage.getItem('useNewDocViewer'))),
			filter(([, useNewDv]) => useNewDv === null || useNewDv === "false"),
			distinctUntilChanged(([aState, ], [bState, ]) => aState.productionId === bState.productionId)
		).subscribe(([current,]) => {
			this.updateProductionId(current.productionId);
		});
	}

	public updateProductionId(id: string): void {
		this.prodId = id;
		this.docViewerStore.currentDocument$.pipe(
			take(1),
			switchMap((currentDocument) => this.buildUrl(currentDocument, DocViewerTab.Production)),
			tap((x) => this.iframeUrlMap.get(DocViewerTab.Production).url.next(x)),
			switchMap(() => this.updateContent(DocViewerTab.Production))
		).subscribe();
	}

	public updateContent(tab: DocViewerTab): Observable<void> {
		const url = this.iframeUrlMap.get(tab).url.value;
		if (!url || ((tab === DocViewerTab.RedactedPdf || tab === DocViewerTab.AnnotatedPdf) && !this.userConfigsService.inRoles(this.roles.LimitedFeatureSet))) return of(null);
		return this.httpClient.get(
			this.config.getConfig().webApi + url,
			{responseType: 'blob', observe: 'response'}
		).pipe(
			tap({
				next: result => {
					this.iframeUrlMap.get(tab).content.next(null);
					this.iframeUrlMap.get(tab).content.next(URL.createObjectURL(result.body));
				},
				error: () => {
					this.iframeUrlMap.get(tab).content.next('');
				}
			}),
			map(() => null),
			catchError(() => of(null))
		);
	}

	public getUrl(key: DocViewerTab): Observable<string> {
		return this.iframeUrlMap.get(key).url;
	}

	public getContent(key: DocViewerTab): Observable<string> {
		return this.iframeUrlMap.get(key).content;
	}

	private updatePageTabPage(page: number): void {
		this.pageTabPage = page;
		this.docViewerStore.currentDocument$.pipe(
			take(1),
			switchMap((currentDocument) => this.buildUrl(currentDocument, DocViewerTab.Page)),
			tap((x) => this.iframeUrlMap.get(DocViewerTab.Page).url.next(x))
		).subscribe();
	}

	private buildUrl(currentDocument: LexbeDoc, tab: DocViewerTab): Observable<string> {
		return this.docViewerStore.simpleState$.pipe(
			take(1),
			switchMap((current) => {

				const tabIndex: MainTabIndices = Object.values(DocViewerTab).findIndex((x) => x === tab);

				if (!this.tabStates.enabled[tabIndex]) {
					return of(null);
				}

				if (this.shouldGenerateTemporaryUrl(tab)) {
					return this.getTemporaryUrl(currentDocument.docId, tab);
				}

				const urlParams = new URLSearchParams();
				switch (tab) {
					default:
						urlParams.append('docId', currentDocument.docId);
						urlParams.append('p', current.legacyParams.page);
						urlParams.append('tab', tab);
						urlParams.append('toolbar', '1');
						urlParams.append('navpanes', '0');

						switch (tab) {
							case DocViewerTab.Text:
								urlParams.delete('p');
								break;
							case DocViewerTab.Page:
								if (this.pageTabPage != null) {
									urlParams.set('p', this.pageTabPage.toString());
								}
								urlParams.set('toolbar', '0');
								break;
							case DocViewerTab.Production:
								if (!this.prodId) {
									return of(null);
								}
								urlParams.delete('p');
								urlParams.set('toolbar', '0');
								if (this.prodId) {
									urlParams.append('prodId', this.prodId);
								} else if (current.legacyParams.prodId) {
									urlParams.append('prodId', current.legacyParams.prodId);
								}
								break;
							case DocViewerTab.RedactedPdf:
							case DocViewerTab.AnnotatedPdf:
								if (this.userConfigsService.inRoles(this.roles.LimitedFeatureSet)) {
									urlParams.delete('p');
									break;
								} else
									return of(null);
							case DocViewerTab.Hits:
							case DocViewerTab.Compare:
								return of(null);
						}

						return of('/api2/doc-serve?' + urlParams.toString());
				}
			})
		);

	}

	private updateTemporaryUrl(docId: string, tab: DocViewerTab): Observable<string> {
		const lastFetch = this.lastFetchTimes[tab];

		if (!lastFetch || ((new Date()).getTime() - lastFetch.getTime()) > 5000) {
			return this.getTemporaryUrl(docId, tab);
		} else {
			return of(null);
		}
	}

	private shouldGenerateTemporaryUrl(tab: DocViewerTab): boolean {
		return tab === DocViewerTab.Pdf || tab === DocViewerTab.Original || tab === DocViewerTab.Html;
	}

	private getTemporaryUrl(docId: string, tab: DocViewerTab): Observable<string> {
		this.lastFetchTimes[tab] = new Date();
		return this.fileSdk.getFileDownloadUrl({
			docId,
			fileType: this.tabFileTypeMap.get(tab)
		}).pipe(
			map((response) => {
				return response.url;
			})
		);
	}


	public buildUrlV2(urlRequest: DocViewerUrlRequest): string {
		const urlParams = new URLSearchParams();

		const tab = documentViewToDocViewerTabMap.get(urlRequest.documentView);
		urlParams.append('tab', tab);
		urlParams.append('toolbar', '1');
		urlParams.append('navpanes', '0');
		urlParams.append('docId', urlRequest.documentId);

		if (urlRequest.page) {
			urlParams.append('p', urlRequest.page.toString());
		}

		if (urlRequest.shouldHideToolbar) {
			urlParams.set('toolbar', '0');
		}

		if (urlRequest.productionJobId) {
			urlParams.append('prodId', urlRequest.productionJobId);
		}

		return `/api2/doc-serve?${urlParams.toString()}`;
	}
}

type UrlAndContent = {
	url: BehaviorSubject<string>,
	content: BehaviorSubject<string>
}

interface DocViewerUrlRequest {
	documentId: string;
	documentView: DocumentViews;
	page?: number;
	shouldHideToolbar?: boolean;
	productionJobId?: string;
}
export const documentViewToDocViewerTabMap = new Map<DocumentViews, string>([
	['original', DocViewerTab.Original],
	['html', DocViewerTab.Html],
	['pdf', DocViewerTab.Pdf],
	['text', DocViewerTab.Text],
	['pages', DocViewerTab.Page],
	['redacted', DocViewerTab.RedactedPdf],
	['annotated', DocViewerTab.AnnotatedPdf],
	['text-compare', DocViewerTab.Compare],
	['translated', DocViewerTab.Translated],
	['produced', DocViewerTab.Production]
]);
